import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, MapPin, Route } from 'lucide-react'
import { apiClient } from '../config/api'
import { vehicleService } from '../services/vehicleService'

export interface PlaceOption {
  name: string
  lat: number
  lng: number
  origin: string
}

interface CorridorOption {
  id: string
  name: string
  startName: string
  endName: string
}

export interface TransportRouteSelection {
  corridorId: string
  corridorName: string
  departure: string
  destination: string
  departurePlace: PlaceOption | null
  destinationPlace: PlaceOption | null
}

export const emptyRouteSelection = (): TransportRouteSelection => ({
  corridorId: '',
  corridorName: '',
  departure: '',
  destination: '',
  departurePlace: null,
  destinationPlace: null
})

/** True when the selection is complete enough to create a transport assignment */
export const routeSelectionComplete = (selection: TransportRouteSelection) =>
  Boolean(selection.corridorId || (selection.departurePlace && selection.destinationPlace))

/** Persist the selection as the vehicle's active transport assignment */
export const applyTransportSelection = async (
  vehicleSource: 'ASSET' | 'CONTAINER',
  vehicleId: number,
  selection: TransportRouteSelection
) => {
  if (selection.corridorId) {
    await vehicleService.setTransport(vehicleSource, vehicleId, { corridor_id: selection.corridorId })
  } else if (selection.departurePlace && selection.destinationPlace) {
    await vehicleService.setTransport(vehicleSource, vehicleId, {
      departure: {
        name: selection.departurePlace.name,
        lat: selection.departurePlace.lat,
        lng: selection.departurePlace.lng
      },
      destination: {
        name: selection.destinationPlace.name,
        lat: selection.destinationPlace.lat,
        lng: selection.destinationPlace.lng
      }
    })
  }
}

interface Props {
  value: TransportRouteSelection
  onChange: (value: TransportRouteSelection) => void
  /** Marks the destination label with * (validated by the parent form) */
  requireDestination?: boolean
}

/**
 * Route picker used everywhere a departure/destination is entered: no free
 * text. Either pick an itinerary (existing trajectory — endpoints auto-fill)
 * or pick departure and destination from the derived places list
 * (trajectory endpoints + geofences).
 */
const TransportRoutePicker: React.FC<Props> = ({ value, onChange, requireDestination = false }) => {
  const { t } = useTranslation()
  const [corridors, setCorridors] = useState<CorridorOption[]>([])
  const [places, setPlaces] = useState<PlaceOption[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    Promise.all([
      apiClient.get('/corridors').catch(() => ({ data: [] })),
      vehicleService.getPlaces().catch(() => [])
    ]).then(([corridorResponse, placeRows]) => {
      if (cancelled) return
      const rows = Array.isArray(corridorResponse.data) ? corridorResponse.data : []
      setCorridors(rows
        .filter((row: any) => !row.type || String(row.type).toLowerCase() === 'trajectory')
        .map((row: any) => {
          const waypoints = Array.isArray(row.waypoints) ? row.waypoints : []
          const first = waypoints[0]
          const last = waypoints[waypoints.length - 1]
          return {
            id: String(row.id),
            name: row.name,
            startName: first?.address || `${row.name} — start`,
            endName: last?.address || `${row.name} — end`
          }
        }))
      setPlaces(placeRows as PlaceOption[])
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [])

  const placeByName = useMemo(() => new Map(places.map(place => [place.name, place])), [places])

  const pickCorridor = (corridorId: string) => {
    const corridor = corridors.find(item => item.id === corridorId) || null
    onChange({
      corridorId: corridor ? corridor.id : '',
      corridorName: corridor?.name || '',
      departure: corridor ? corridor.startName : '',
      destination: corridor ? corridor.endName : '',
      departurePlace: null,
      destinationPlace: null
    })
  }

  const pickPlace = (kind: 'departure' | 'destination', name: string) => {
    const place = placeByName.get(name) || null
    onChange({
      ...value,
      corridorId: '',
      corridorName: '',
      [kind]: name,
      [`${kind}Place`]: place
    } as TransportRouteSelection)
  }

  if (loading) {
    return (
      <div className="py-4 flex items-center justify-center text-slate-400">
        <Loader2 size={16} className="animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-slate-700">
        <span className="inline-flex items-center gap-1.5">
          <Route size={14} className="text-blue-500" />
          {t('transport_itinerary', { defaultValue: 'Itinerary (trajectory)' })}
        </span>
        <select
          value={value.corridorId}
          onChange={event => pickCorridor(event.target.value)}
          className="mt-1.5 w-full px-3 py-2.5 rounded-xl border border-slate-300 bg-white text-sm"
        >
          <option value="">{t('transport_no_itinerary_option', { defaultValue: 'No itinerary — pick departure and destination below' })}</option>
          {corridors.map(corridor => (
            <option key={corridor.id} value={corridor.id}>{corridor.name}</option>
          ))}
        </select>
        {value.corridorId && (
          <span className="mt-1 block text-xs text-slate-500">
            {value.departure} → {value.destination}
          </span>
        )}
      </label>

      {!value.corridorId && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-sm font-medium text-slate-700">
            <span className="inline-flex items-center gap-1.5">
              <MapPin size={14} className="text-slate-400" />
              {t('transport_departure', { defaultValue: 'Departure' })}
            </span>
            <select
              value={value.departure}
              onChange={event => pickPlace('departure', event.target.value)}
              className="mt-1.5 w-full px-3 py-2.5 rounded-xl border border-slate-300 bg-white text-sm"
            >
              <option value="">{t('transport_select_place', { defaultValue: 'Select a place…' })}</option>
              {places.map(place => (
                <option key={place.name} value={place.name}>{place.name}</option>
              ))}
            </select>
          </label>
          <label className="text-sm font-medium text-slate-700">
            <span className="inline-flex items-center gap-1.5">
              <MapPin size={14} className="text-blue-500" />
              {t('transport_destination', { defaultValue: 'Destination' })}{requireDestination ? ' *' : ''}
            </span>
            <select
              value={value.destination}
              onChange={event => pickPlace('destination', event.target.value)}
              className="mt-1.5 w-full px-3 py-2.5 rounded-xl border border-slate-300 bg-white text-sm"
            >
              <option value="">{t('transport_select_place', { defaultValue: 'Select a place…' })}</option>
              {places.map(place => (
                <option key={place.name} value={place.name}>{place.name}</option>
              ))}
            </select>
          </label>
          {places.length === 0 && (
            <p className="sm:col-span-2 text-xs text-slate-500">
              {t('transport_no_places', { defaultValue: 'No places yet — places come from trajectory endpoints and geofences.' })}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export default TransportRoutePicker
